/**
 * PASSO 4 — ROMANEIO. O papel que vai para o vendedor: PDF (imprimir), Excel ou WhatsApp.
 *
 * O romaneio NUNCA mostra comissão, frete nem custo de chegada: isso é do
 * comprador, e mora na aba Custo.
 *
 * 3D · A GTA SE ANOTA AQUI, depois da pesagem — «não se emite GTA antes de
 * pesar o gado» —, uma por porteira, porque cada destino (cada caminhão) tem a
 * sua. Sem porteira, ou com uma só, é um campo só. O romaneio do vendedor
 * leva todas; o papel do cliente (aba Custo), só a dele. Grava sozinho, também
 * em ficha encerrada: a GTA não é negociação nem peso, e costuma sair depois
 * de encerrar.
 *
 * 3D · DESCONTOS POR LOTE: lotes com descontos iguais saem como sempre
 * («Financeiro (2% do bruto)»); diferentes, uma linha por lote.
 *
 * 3E (test drive de 06/10/2026):
 *  - SEM ROMANEIO PARCIAL: ficha aberta mostra a Prévia (a mesma da pesagem)
 *    e o botão de encerrar. O papel só existe depois de encerrar — assim,
 *    reabrir para corrigir e voltar aqui nunca mostra um «parcial» com cara de
 *    documento; e uma versão nova da negociação (ficha encerrada) cai direto
 *    no romaneio final dela;
 *  - sem as coordenadas de GPS no papel («não faz sentido» — continuam na
 *    ficha e no cadastro);
 *  - a relação de pesagens economiza papel: só lote curto, quantidade e peso,
 *    em 3 blocos lado a lado na impressão; a data vai uma vez, no cabeçalho;
 *  - a Consulta vê o papel e exporta, sem Reabrir, Cancelar nem GTA editável.
 *
 * 3F · DOIS PAPÉIS COM OS MESMOS PESOS (porteira Revenda, depois de encerrar):
 *  - romaneio de COMPRA, para o vendedor: o de sempre — todos os animais, os
 *    preços de compra e todas as GTAs;
 *  - romaneio de VENDA, para o comprador final: só os animais da porteira
 *    dele, os preços e descontos da venda e só a GTA dele; vendedor = a
 *    empresa (emitente), comprador = o comprador final do cadastro.
 *  Nenhum dos dois mostra o ágio. O de venda só existe para quem vê o preço de
 *  venda (gestor e Consulta); o comprador (funcionário) nem vê a escolha.
 *
 * R2B (revisão de 06/10/2026): OS TRÊS PAPÉIS JUNTOS aqui — compra (vendedor),
 * venda (comprador final, número com «-V») e cliente (intermediação, dois
 * blocos: «pague ao vendedor» e «pague a <empresa>»). O do cliente saiu da aba
 * Custo; quem não vê comissão imprime sem ela.
 *
 * R1C (revisão de UX de 06/10/2026):
 *  - a GTA vem primeiro (é o que se anota no curral); depois PDF, WhatsApp e
 *    Excel; «Reabrir» e «Cancelar ficha» numa linha separada, no fim, com o
 *    «Cancelar» menor — longe do dedo de quem só quer mandar o PDF;
 *  - reabrir e cancelar abrem a janela do app com motivo OBRIGATÓRIO e
 *    atalhos de um toque («boi garimpado depois da lida», «boi fugiu no
 *    embarque», «peso digitado errado»);
 *  - o papel do vendedor ganha a linha do pagamento COMBINADO (forma e
 *    vencimento — nunca a quitação), a média só dos pesados e, com quebra,
 *    o peso da balança, a quebra e o peso que vale.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { baixar } from '../dados/arquivos'
import { lerEstado, mudarFicha, usarEstado } from '../dados/armazem'
import { cancelar, reabrir } from '../dados/acoes'
import { calcular, romaneioParcial } from '../dominio/contas'
import { contasPorPorteira, nomeDaPorteira, porteirasDa } from '../dominio/destinos'
import {
  abasDoCliente, abasDoRomaneio, abasDoRomaneioDeVenda, destinoDoVendedor, gtasDa, linhasDeDesconto, lugar, numeroDaVenda, periodo, relacaoDePesagens, romaneioDeVenda, temQuebra, textoDaMedia,
  textoDoCombinado, textoDoResumo,
  type RomaneioDeVenda,
} from '../dominio/exportar'
import { comissaoDoCliente, usarPagamentos, vendaDaPorteira } from '../dados/pagamentos'
import { PapelDoCliente } from './PapelDoCliente'
import { normalizar } from '../dominio/numeros'
import { agora, kg, num, quando, reais } from '../dominio/numeros'
import { montarXlsx, nomeDeArquivo } from '../dominio/planilha'
import { BASES, type Emitente, type Ficha, type Produtor } from '../dominio/tipos'
import { avisar, Campo, pedirMotivoNaJanela, Segmento, SinalDeSalvo, Tarja, usarGravacaoAutomatica, type Ir } from '../ui'
import { usarPode } from '../dados/sessao'
import { pedirEncerramento, Previa, Totais } from './Pesar'

/**
 * R1C · Reabrir com motivo obrigatório, na janela do app. Usado aqui, na
 * Pesagem e na Origem. Devolve true se reabriu.
 */
export async function pedirReabertura(ficha: Ficha): Promise<boolean> {
  const m = await pedirMotivoNaJanela({
    titulo: `Reabrir a ficha nº ${ficha.numero}?`,
    texto: 'Para acrescentar ou corrigir animais. O romaneio vai mostrar que ela foi reaberta, com o motivo.',
    sim: 'Reabrir a ficha',
  })
  if (m === null) return false
  reabrir(ficha.id, m)
  avisar('Ficha reaberta')
  return true
}

/** R1C · Cancelar com motivo obrigatório, na janela do app. */
export async function pedirCancelamento(ficha: Ficha): Promise<boolean> {
  const m = await pedirMotivoNaJanela({
    titulo: `Cancelar a ficha nº ${ficha.numero}?`,
    texto: 'Ela continua nas Fichas, marcada como cancelada, fora dos totais. Nada é apagado.',
    sim: 'Cancelar a ficha', perigo: true,
  })
  if (m === null) return false
  cancelar(ficha.id, m)
  avisar('Ficha cancelada')
  return true
}

/** A ficha com o emitente valendo: o gravado ao encerrar, ou o dos Ajustes enquanto aberta. */
export function comEmitente(f: Ficha, atual: Emitente): Ficha {
  return { ...f, emitente: f.situacao === 'aberta' || !f.emitente ? atual : f.emitente }
}

/** Folha com largura de papel; no celular ela encolhe inteira para caber, como prévia de impressão. */
export function Folha({ children }: { children: ReactNode }) {
  const caixa = useRef<HTMLDivElement>(null)
  const folha = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const ajustar = () => {
      const c = caixa.current, f = folha.current
      if (!c || !f) return
      f.style.zoom = ''
      const z = c.clientWidth / f.offsetWidth
      if (z < 1) f.style.zoom = z.toFixed(3)
    }
    ajustar()
    window.addEventListener('resize', ajustar)
    return () => window.removeEventListener('resize', ajustar)
  })
  return <div className="folha-caixa" ref={caixa}><div className="folha" ref={folha}>{children}</div></div>
}

function Parte({ titulo, x }: { titulo: string; x: Partial<Produtor> & { cidade?: string } }) {
  const lin = (r: string, v?: string) => (v ? <div><span>{r}</span> {v}</div> : null)
  return (
    <td>
      <div className="p-titulo">{titulo}</div>
      <div className="p-nome">{x.nome || '—'}</div>
      {lin('CPF/CNPJ:', x.doc)}{lin('Inscr. estadual:', x.ie)}{lin('Propriedade:', x.propriedade)}{lin('Endereço:', x.endereco)}
      {lin('Município:', [x.municipio || x.cidade, x.uf].filter(Boolean).join('/'))}{lin('Telefone:', x.telefone)}
    </td>
  )
}

export function PapelDoRomaneio({ f }: { f: Ficha }) {
  const r = calcular(f)
  const em = f.emitente ?? { nome: '', doc: '', telefone: '', cidade: '' }
  type Lado = [string, Partial<Produtor> & { cidade?: string }]
  const partes: [Lado, Lado] =
    f.finalidade === 'Venda' ? [['Vendedor', em], ['Comprador', f.produtor]]
      : f.finalidade === 'Compra' ? [['Vendedor', f.produtor], ['Comprador', em]]
        : [['Proprietário dos animais', f.produtor], ['Responsável pela pesagem', em]]
  const linhas = r.linhas.filter((l) => l.cab > 0)
  const cancelamento = f.historico.filter((h) => h.oque.startsWith('Cancelada')).pop()
  const marcas = f.historico.filter((h) => /^(Reaberta|Versão|Cancelada)/.test(h.oque))
  const relacao = relacaoDePesagens(f)
  // 3D: ficha nova não tem destino na abertura (o destino virou porteira, que o vendedor não vê)
  const destino = destinoDoVendedor(f)
  const gtas = gtasDa(f)
  // R1C: com quebra, o papel mostra o peso da balança, a quebra e o peso que vale
  const q = temQuebra(r)
  const combinado = textoDoCombinado(f)
  return (
    <div className="papel">
      <div className="p-cabeca">
        <div>
          <h3>Romaneio de pesagem de bovinos</h3>
          <div className="p-sub">{[em.nome, em.doc, em.telefone, em.cidade].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="p-numero"><b>Nº {f.numero}</b>{f.versao > 1 && <> · versão {f.versao}</>}<br />{periodo(f)}<br />{f.finalidade}</div>
      </div>
      {f.situacao === 'cancelada' && <div className="carimbo vermelho">{(cancelamento?.oque ?? 'CANCELADA').toUpperCase()}</div>}
      <table className="p-partes"><tbody><tr><Parte titulo={partes[0][0]} x={partes[0][1]} /><Parte titulo={partes[1][0]} x={partes[1][1]} /></tr></tbody></table>
      {/* 3E: sem GPS no papel — a localização fica na ficha e no cadastro */}
      {/* R2B: na Venda o gado sai da empresa e vai para o comprador */}
      {f.finalidade === 'Venda'
        ? <div className="p-linha"><b>De onde sai o gado:</b> {em.cidade || em.nome || '—'} &nbsp;→&nbsp; <b>Destino:</b> {destino || lugar(f.produtor) || '—'}</div>
        : <div className="p-linha"><b>Origem:</b> {lugar(f.produtor) || '—'}
          {destino && <> &nbsp;→&nbsp; <b>Destino:</b> {destino}</>}</div>}
      {gtas.length > 0 && <div className="p-linha"><b>GTA nº</b> {gtas.join(', ')}</div>}
      {/* R1C: só o COMBINADO (forma e vencimento) — o que já foi pago nunca sai no papel */}
      {combinado && <div className="p-linha"><b>Pagamento combinado:</b> {combinado}</div>}

      <h4>Resumo por lote</h4>
      <table>
        <thead><tr>
          <th>Lote</th><th className="n">Cab.</th><th className="n">{q ? 'Peso da balança (kg)' : 'Peso total (kg)'}</th><th className="n">Média (kg)</th>
          {q && <><th className="n">Quebra</th><th className="n">Peso que vale (kg)</th></>}
          {r.temArroba && <><th className="n">Rend.</th><th className="n">Arrobas</th></>}
          {r.temPreco && <><th className="n">Preço</th><th className="n">Valor</th></>}
        </tr></thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.lote.id}>
              <td>{l.lote.categoria} <span className="p-base">{BASES[l.lote.base].nome.toLowerCase()}</span></td>
              <td className="n">{l.cab}</td>
              <td className="n">{l.kg ? kg(l.kg) : '—'}</td>
              {/* R1C: a média é só dos pesados, e diz quantos quando falta peso de alguém */}
              <td className="n">{textoDaMedia(l.kg, l.cab, l.cabPesadas)?.replace(' kg', '') ?? '—'}</td>
              {q && <><td className="n">{l.quebraPct ? num(l.quebraPct, 2) + '%' : ''}</td><td className="n">{l.quebraPct ? kg(l.kgQueVale) : ''}</td></>}
              {r.temArroba && <><td className="n">{l.lote.base === 'arroba' ? num(l.lote.rendimento, 1) + '%' : ''}</td>
                <td className="n">{l.arrobas === null ? '' : num(l.arrobas, 2)}</td></>}
              {r.temPreco && <><td className="n">{l.lote.preco ? `${num(l.lote.preco, 2)} ${BASES[l.lote.base].unidade.replace('R$', '').trim()}` : '—'}</td>
                <td className="n">{reais(l.valor)}</td></>}
            </tr>
          ))}
          {!linhas.length && <tr><td colSpan={8}>Nenhum animal registrado.</td></tr>}
        </tbody>
        <tfoot><tr>
          <th>Total</th><th className="n">{r.cab}</th><th className="n">{r.kg ? kg(r.kg) : '—'}</th><th className="n">{r.media === null ? '—' : num(r.media, 1)}</th>
          {q && <><th /><th /></>}
          {r.temArroba && <><th /><th className="n">{num(r.arrobas, 2)}</th></>}
          {r.temPreco && <><th /><th className="n">{reais(r.bruto)}</th></>}
        </tr></tfoot>
      </table>
      {r.temArroba && <div className="p-nota">Arroba de 15 kg de carcaça: arrobas = {q ? 'peso que vale' : 'peso vivo'} × rendimento ÷ 15{f.arrobaEmDuasCasas ? ', em 2 casas, e é esse número que multiplica o preço' : ''}.</div>}
      {q && <div className="p-nota">Quebra combinada: peso que vale = peso da balança × (100% − quebra).</div>}
      {r.temPreco && (
        <table className="p-valores"><tbody>
          <tr><td>Valor bruto</td><td className="n">{reais(r.bruto)}</td></tr>
          {linhasDeDesconto(r).map((d) => <tr key={d.rotulo}><td>{d.rotulo}</td><td className="n">− {reais(d.valor)}</td></tr>)}
          <tr className="liquido"><td>Valor líquido</td><td className="n">{reais(r.liquido)}</td></tr>
        </tbody></table>
      )}
      {r.temPreco && !r.descontosIguais && (r.financeiro > 0 || r.funrural > 0) && (
        <div className="p-nota">Descontos por lote, cada um sobre o bruto do próprio lote. Somados: desconto à vista {reais(r.financeiro)} · desconto de Funrural {reais(r.funrural)}.</div>
      )}
      {r.semValor.length > 0 && <div className="p-nota">Sem valor calculado (sem preço ou com animal sem peso): {r.semValor.join(', ')}.</div>}

      <h4>Relação de pesagens ({f.registros.length} registro{f.registros.length === 1 ? '' : 's'} · {periodo(f)})</h4>
      {/* 3E: só lote, quantidade e peso; 3 blocos lado a lado na impressão (estilo.css) */}
      {/* o cabeçalho, um por bloco (na tela estreita, um só) */}
      <div className={`p-rel-cabecas${relacao.mostraQtd ? '' : ' sem-qtd'}`}>
        {[0, 1, 2].map((i) => (
          <div className="p-rel-cab" key={i}><span>Lote</span>{relacao.mostraQtd && <span className="n">Cab.</span>}<span className="n">Peso (kg)</span></div>
        ))}
      </div>
      <div className={`p-relacao${relacao.mostraQtd ? '' : ' sem-qtd'}`}>
        {relacao.linhas.map((x) => (
          <div className="p-rel-linha" key={x.id}>
            <span>{x.lote}</span>{relacao.mostraQtd && <span className="n">{x.qtd ?? '—'}</span>}<span className="n">{x.kg === null ? '—' : kg(x.kg)}</span>
          </div>
        ))}
      </div>
      {f.obs && <><h4>Observações</h4><div className="p-obs">{f.obs}</div></>}
      {marcas.length > 0 && <div className="p-nota" style={{ marginTop: 10 }}><b>Histórico:</b> {marcas.map((h) => `${quando(h.em)} — ${h.oque}`).join(' · ')}</div>}
      <div className="p-assinaturas">
        <div>{partes[0][0]}<br />{partes[0][1].nome}</div>
        <div>{partes[1][0]}<br />{partes[1][1].nome}</div>
      </div>
      {f.pesadoPor && <div className="p-nota" style={{ textAlign: 'center', marginTop: 6 }}><b>Pesado por:</b> {f.pesadoPor}</div>}
      <div className="p-rodape">
        {f.situacao === 'encerrada'
          ? `Relatório emitido via ${em.nome || 'Pesagem'} em ${quando(f.historico.filter((h) => h.oque === 'Encerrada').pop()?.em)}${f.pesadoPor ? ` por ${f.pesadoPor}` : ''}.`
          : `Documento parcial gerado via ${em.nome || 'Pesagem'} em ${quando(new Date().toISOString())}.`}
        {' '}Documento de controle de pesagem. Não substitui nota fiscal nem GTA.</div>
    </div>
  )
}

export function Romaneio({ ficha, ir }: { ficha: Ficha; ir: Ir }) {
  const { ajustes, produtores } = usarEstado()
  const pode = usarPode()
  const pg = usarPagamentos()
  const f = comEmitente(ficha, ajustes.emitente)
  // 3F: os romaneios de venda (porteiras Revenda), só para quem vê o preço de venda
  const vendas: RomaneioDeVenda[] = pode('verVenda') && f.finalidade === 'Compra' && porteirasDa(f).some((p) => p.tipo === 'revenda')
    ? contasPorPorteira(f, { verComissao: true, vendaDe: (d) => vendaDaPorteira(pg, f.id, d) }).porteiras
      .map((p) => romaneioDeVenda(f, p, p.id ? vendaDaPorteira(pg, f.id, p.id) : null)).filter((x): x is RomaneioDeVenda => !!x)
    : []
  // R2B: o papel de cada cliente (intermediação) também mora aqui; quem não vê comissão imprime sem ela
  const verComissao = pode('verComissao')
  const clientes = f.finalidade === 'Compra' && porteirasDa(f).some((p) => p.tipo === 'cliente')
    ? contasPorPorteira(f, { verComissao, comissaoCliente: (d) => comissaoDoCliente(pg, f.id, d) }).porteiras.filter((p) => p.porteira?.tipo === 'cliente' && p.id)
    : []
  const [papel, setPapel] = useState('compra')
  const rv = vendas.find((x) => x.conta.id === papel) ?? null
  const cli = papel.startsWith('cliente:') ? clientes.find((p) => `cliente:${p.id}` === papel) ?? null : null
  const comprador = rv ? compradorFinal(rv, produtores) : null
  const nome = rv
    ? nomeDeArquivo('romaneio-venda', numeroDaVenda(f.numero), rv.conta.porteira?.nome || 'comprador-final')
    : cli ? nomeDeArquivo('cliente', f.numero, cli.nome)
      : nomeDeArquivo('romaneio', f.numero, f.produtor.propriedade || f.produtor.nome)

  function imprimir() {
    const titulo = document.title
    document.title = nome // vira o nome do PDF salvo
    window.print()
    setTimeout(() => { document.title = titulo }, 1000)
  }
  function excel() {
    const abas = rv && comprador ? abasDoRomaneioDeVenda(f, rv, comprador, f.emitente) : cli ? abasDoCliente(f, cli, !verComissao, f.emitente?.nome || ajustes.emitente.nome) : abasDoRomaneio(f)
    baixar(montarXlsx(abas), nome + '.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    avisar('Planilha salva em Downloads')
  }
  async function enviar() {
    const t = textoDoResumo(f)
    if (navigator.share) {
      try { await navigator.share({ text: t }); return } catch (e) { if ((e as Error).name === 'AbortError') return }
    }
    try { await navigator.clipboard.writeText(t); avisar('Resumo copiado. É só colar no WhatsApp.') }
    catch { window.open('https://wa.me/?text=' + encodeURIComponent(t), '_blank') }
  }

  // 3E: ficha aberta não tem papel — tem a prévia e o caminho para encerrar
  if (romaneioParcial(ficha)) {
    return (
      <div>
        <Tarja>
          Pesagem em andamento. O romaneio sai ao encerrar{ficha.historico.some((h) => h.oque.startsWith('Reaberta')) ? ' de novo (a ficha foi reaberta)' : ''}.
        </Tarja>
        <Totais ficha={ficha} />
        <Previa ficha={ficha} sempreAberta />
        {pode('encerrar') && <button className="botao" onClick={async () => { if (await pedirEncerramento(ficha)) avisar('Ficha encerrada') }}>Encerrar e ver o romaneio</button>}
        {pode('pesar') && <button className="botao fantasma" onClick={() => ir({ tela: 'ficha', id: ficha.id, aba: 'pesagem' })}>Continuar pesando</button>}
        {pode('anotarGta') && <CamposDeGta key={ficha.id + ficha.situacao} ficha={ficha} />}
      </div>
    )
  }

  const podeReabrir = ficha.situacao === 'encerrada' && pode('reabrir')
  const podeCancelar = ficha.situacao !== 'cancelada' && pode('cancelarFicha', { encerrada: ficha.situacao === 'encerrada' })
  return (
    <div>
      {/* R1C: a GTA primeiro — é o que se anota no curral, antes de mandar o papel */}
      {ficha.situacao !== 'cancelada' && pode('anotarGta') && <CamposDeGta key={ficha.id + ficha.situacao} ficha={ficha} />}
      <div className="barra tres nao-imprimir">
        <button className="botao" onClick={imprimir}>PDF / imprimir</button>
        {!rv && !cli && <button className="botao fantasma" onClick={enviar}>WhatsApp</button>}
        <button className="botao fantasma" onClick={excel}>Excel</button>
      </div>
      {!ajustes.emitente.nome && pode('dadosDaEmpresa') && (
        <div className="nao-imprimir"><Tarja onClick={() => ir({ tela: 'ajustes' })}>Seu nome está em branco nos Ajustes — o romaneio sai sem o comprador. Toque para preencher.</Tarja></div>
      )}
      {(vendas.length > 0 || clientes.length > 0) && (
        <div className="nao-imprimir" style={{ marginTop: 8 }}>
          <div className="mini-dica">Papel na tela (é o que o PDF e o Excel levam). Todos têm os mesmos pesos; nenhum mostra o ágio.</div>
          <Segmento valor={papel} mudar={setPapel}
            opcoes={[['compra', 'Compra (vendedor)'],
              ...vendas.map((x): [string, string] => [x.conta.id ?? '', `Venda · ${x.conta.porteira?.nome || 'comprador final'}`]),
              ...clientes.map((p): [string, string] => [`cliente:${p.id}`, `Cliente · ${p.nome}`])]} />
        </div>
      )}
      <div className="dica nao-imprimir">PDF: toque em <b>PDF / imprimir</b> e escolha <b>Salvar como PDF</b>. O arquivo já sai com o nome da ficha.</div>
      <Folha>{rv && comprador ? <PapelDoRomaneioDeVenda f={f} rv={rv} comprador={comprador} />
        : cli ? <PapelDoCliente ficha={f} p={cli} emitente={f.emitente?.nome || ajustes.emitente.nome} semComissao={!verComissao} />
          : <PapelDoRomaneio f={f} />}</Folha>
      {/* R1C: reabrir e cancelar numa linha à parte, no fim, longe dos botões de mandar o papel */}
      {(podeReabrir || podeCancelar) && (
        <div className="acoes-de-risco nao-imprimir">
          {podeReabrir && <button className="botao fantasma" onClick={async () => {
            if (await pedirReabertura(ficha)) ir({ tela: 'ficha', id: ficha.id, aba: 'pesagem' })
          }}>Reabrir</button>}
          {podeCancelar && <button className="botao perigo pequeno" onClick={() => { void pedirCancelamento(ficha) }}>Cancelar ficha</button>}
        </div>
      )}
    </div>
  )
}

/** O comprador final do cadastro de clientes (pelo id gravado na porteira, ou pelo nome); sem cadastro, o nome da porteira. */
function compradorFinal(rv: RomaneioDeVenda, produtores: Produtor[]): Partial<Produtor> {
  const p = rv.conta.porteira
  const achado = produtores.find((x) => x.id === p?.clienteId) ?? produtores.find((x) => !!p?.nome && normalizar(x.nome) === normalizar(p.nome))
  return achado ?? { nome: p?.nome ?? '', municipio: p?.municipio ?? '', uf: p?.uf ?? '' }
}

/**
 * 3F · O romaneio de VENDA, para o comprador final: só os animais da porteira
 * dele, aos preços e descontos da VENDA, e só a GTA dele. Vendedor = a
 * empresa (emitente); comprador = o comprador final. Nada do preço de
 * compra, do vendedor de origem nem do ágio.
 */
export function PapelDoRomaneioDeVenda({ f, rv, comprador }: { f: Ficha; rv: RomaneioDeVenda; comprador: Partial<Produtor> }) {
  const r = rv.resultado
  const em = f.emitente ?? { nome: '', doc: '', telefone: '', cidade: '' }
  const linhas = r.linhas.filter((l) => l.cab > 0)
  return (
    <div className="papel">
      <div className="p-cabeca">
        <div>
          <h3>Romaneio de venda de bovinos</h3>
          <div className="p-sub">{[em.nome, em.doc, em.telefone, em.cidade].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="p-numero"><b>Nº {numeroDaVenda(f.numero)}</b><br />{periodo(f)}<br />Venda</div>
      </div>
      <table className="p-partes"><tbody><tr><Parte titulo="Vendedor" x={em} /><Parte titulo="Comprador" x={comprador} /></tr></tbody></table>
      <div className="p-linha"><b>Destino:</b> {lugar(comprador) || '—'}</div>
      {rv.gta && <div className="p-linha"><b>GTA nº</b> {rv.gta}</div>}

      <h4>Resumo por lote</h4>
      <table>
        <thead><tr>
          <th>Lote</th><th className="n">Cab.</th><th className="n">Peso total (kg)</th><th className="n">Média (kg)</th>
          {r.temArroba && <><th className="n">Rend.</th><th className="n">Arrobas</th></>}
          {r.temPreco && <><th className="n">Preço</th><th className="n">Valor</th></>}
        </tr></thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.lote.id}>
              <td>{l.lote.categoria} <span className="p-base">{BASES[l.lote.base].nome.toLowerCase()}</span></td>
              <td className="n">{l.cab}</td>
              <td className="n">{l.kg ? kg(l.kg) : '—'}</td>
              <td className="n">{textoDaMedia(l.kg, l.cab, l.cabPesadas)?.replace(' kg', '') ?? '—'}</td>
              {r.temArroba && <><td className="n">{l.lote.base === 'arroba' ? num(l.lote.rendimento, 1) + '%' : ''}</td>
                <td className="n">{l.arrobas === null ? '' : num(l.arrobas, 2)}</td></>}
              {r.temPreco && <><td className="n">{l.lote.preco ? `${num(l.lote.preco, 2)} ${BASES[l.lote.base].unidade.replace('R$', '').trim()}` : '—'}</td>
                <td className="n">{reais(l.valor)}</td></>}
            </tr>
          ))}
          {!linhas.length && <tr><td colSpan={8}>Nenhum animal nesta porteira.</td></tr>}
        </tbody>
        <tfoot><tr>
          <th>Total</th><th className="n">{r.cab}</th><th className="n">{r.kg ? kg(r.kg) : '—'}</th><th className="n">{r.media === null ? '—' : num(r.media, 1)}</th>
          {r.temArroba && <><th /><th className="n">{num(r.arrobas, 2)}</th></>}
          {r.temPreco && <><th /><th className="n">{reais(r.bruto)}</th></>}
        </tr></tfoot>
      </table>
      {r.temArroba && <div className="p-nota">Arroba de 15 kg de carcaça: arrobas = peso vivo × rendimento ÷ 15.</div>}
      {r.temPreco && (
        <table className="p-valores"><tbody>
          <tr><td>Valor bruto</td><td className="n">{reais(r.bruto)}</td></tr>
          {linhasDeDesconto(r).map((d) => <tr key={d.rotulo}><td>{d.rotulo}</td><td className="n">− {reais(d.valor)}</td></tr>)}
          <tr className="liquido"><td>Valor líquido</td><td className="n">{reais(r.liquido)}</td></tr>
        </tbody></table>
      )}
      {!r.temPreco && rv.valorTotal !== null && (
        <table className="p-valores"><tbody><tr className="liquido"><td>Valor total combinado</td><td className="n">{reais(rv.valorTotal)}</td></tr></tbody></table>
      )}
      {r.semValor.length > 0 && <div className="p-nota">Sem valor calculado (sem preço ou com animal sem peso): {r.semValor.join(', ')}.</div>}

      <h4>Relação de pesagens ({rv.relacao.linhas.length} registro{rv.relacao.linhas.length === 1 ? '' : 's'} · {periodo(f)})</h4>
      <div className={`p-rel-cabecas${rv.relacao.mostraQtd ? '' : ' sem-qtd'}`}>
        {[0, 1, 2].map((i) => (
          <div className="p-rel-cab" key={i}><span>Lote</span>{rv.relacao.mostraQtd && <span className="n">Cab.</span>}<span className="n">Peso (kg)</span></div>
        ))}
      </div>
      <div className={`p-relacao${rv.relacao.mostraQtd ? '' : ' sem-qtd'}`}>
        {rv.relacao.linhas.map((x) => (
          <div className="p-rel-linha" key={x.id}>
            <span>{x.lote}</span>{rv.relacao.mostraQtd && <span className="n">{x.qtd ?? '—'}</span>}<span className="n">{x.kg === null ? '—' : kg(x.kg)}</span>
          </div>
        ))}
      </div>
      <div className="p-assinaturas">
        <div>Vendedor<br />{em.nome}</div>
        <div>Comprador<br />{comprador.nome}</div>
      </div>
      <div className="p-rodape">
        Romaneio de venda emitido via {em.nome || 'Pesagem'} em {quando(new Date().toISOString())}. Documento de controle de pesagem. Não substitui nota fiscal nem GTA.
      </div>
    </div>
  )
}

/**
 * 3D · O nº da GTA, depois da pesagem: um campo por porteira (cada destino tem
 * a sua). Sem porteira, ou com uma só, um campo só. Ficha antiga com a GTA
 * anotada na abertura mostra aquela também, para corrigir se precisar.
 */
function CamposDeGta({ ficha }: { ficha: Ficha }) {
  const ps = porteirasDa(ficha)
  const [geral, setGeral] = useState(ficha.gta ?? '')
  const [por, setPor] = useState<Record<string, string>>(() => Object.fromEntries(ps.map((p) => [p.id, p.gta ?? ''])))
  const rascunho = { geral: geral.trim(), por: Object.fromEntries(ps.map((p) => [p.id, (por[p.id] ?? '').trim()])) }
  const antes = useRef(gtasDa(ficha).join(', '))
  const { situacao } = usarGravacaoAutomatica({
    ativo: true,
    chave: JSON.stringify(rascunho),
    gravar: () => {
      const f = lerEstado().fichas.find((x) => x.id === ficha.id)
      if (!f || f.situacao === 'cancelada') return
      const igual = (f.gta ?? '') === rascunho.geral
        && porteirasDa(f).every((p) => !(p.id in rascunho.por) || (p.gta ?? '') === rascunho.por[p.id])
      if (igual) return
      mudarFicha(ficha.id, (x) => {
        x.gta = rascunho.geral
        if (Array.isArray(x.porteiras)) {
          x.porteiras = x.porteiras.map((p) => {
            if (!(p.id in rascunho.por)) return p
            const { gta: _g, ...resto } = p
            return rascunho.por[p.id] ? { ...resto, gta: rascunho.por[p.id] } : resto
          })
        }
      })
    },
    // uma linha no histórico ao sair, com as GTAs como ficaram (rastreio)
    aoSair: () => {
      const f = lerEstado().fichas.find((x) => x.id === ficha.id)
      if (!f) return
      const agoraSao = gtasDa(f).join(', ')
      if (agoraSao === antes.current) return
      mudarFicha(ficha.id, (x) => { x.historico.push({ em: agora(), oque: `GTA: ${agoraSao || 'nenhuma'}` }) })
      antes.current = agoraSao
    },
  })

  // sem porteira: o campo da ficha. Com porteiras: um por porteira (e o antigo, se houver).
  const mostraGeral = ps.length === 0 || !!ficha.gta
  return (
    <div className="nao-imprimir">
      <h2 className="secao">GTA</h2>
      <div className="cartao">
        {mostraGeral && <Campo rotulo={ps.length ? 'Nº da GTA anotada na abertura' : 'Nº da GTA'} valor={geral} mudar={setGeral} dica="opcional" />}
        {ps.map((p) => (
          <Campo key={p.id} rotulo={ps.length > 1 ? `Nº da GTA — ${nomeDaPorteira(p)}` : 'Nº da GTA'} valor={por[p.id] ?? ''}
            mudar={(v) => setPor({ ...por, [p.id]: v })} dica="opcional" />
        ))}
        <div className="rodape-passo">
          <span className="mini-dica" style={{ marginTop: 0 }}>
            {ps.length > 1 ? 'Uma por porteira. O romaneio do vendedor leva todas; o papel de cada cliente, só a dele.' : 'A GTA sai depois da pesagem e vai no romaneio.'}
          </span>
          <SinalDeSalvo situacao={situacao} />
        </div>
      </div>
    </div>
  )
}
